import { collection, onSnapshot, type Unsubscribe } from "firebase/firestore";
import { getFirestoreDB } from "../Config/firebaseConfig";
import type { JobReference, JobSignal } from "./JobRefreshCoordinator";

const identifier = /^[a-zA-Z0-9_.:-]{1,128}$/;
const kind = /^[a-z][a-z0-9_]{0,63}$/;

export function parseJobReference(input: unknown): JobReference | null {
  if (!input || typeof input !== "object") return null;
  const value = input as Record<string, unknown>;
  if ((value.owner !== "database_service" && value.owner !== "account_orchestrator") ||
      typeof value.kind !== "string" || !kind.test(value.kind) ||
      typeof value.id !== "string" || !identifier.test(value.id) ||
      typeof value.attempt !== "string" || !identifier.test(value.attempt)) return null;
  return { owner: value.owner, kind: value.kind, id: value.id, attempt: value.attempt };
}

export function parseJobSignal(input: unknown): JobSignal | null {
  if (!input || typeof input !== "object") return null;
  const value = input as Record<string, unknown>;
  const job = parseJobReference(value.job);
  if (!job || value.schema_version !== 1 || typeof value.client_id !== "string" ||
      !identifier.test(value.client_id) || typeof value.version !== "number" ||
      !Number.isSafeInteger(value.version) || value.version < 1 ||
      !Array.isArray(value.resources) || value.resources.length > 32) return null;
  const resources: JobSignal["resources"] = [];
  for (const resource of value.resources) {
    if (!resource || typeof resource.kind !== "string" || !kind.test(resource.kind) ||
        typeof resource.id !== "string" || !identifier.test(resource.id)) return null;
    resources.push({ kind: resource.kind, id: resource.id });
  }
  return { schema_version: 1, job, client_id: value.client_id, version: value.version, resources };
}

/** One user feed; never subscribe to another user's or a client-wide job feed. */
export function listenToJobSignals(
  userId: string,
  changed: (signal: JobSignal) => void,
  connection: (connected: boolean) => void,
  removed?: (signal: JobSignal) => void,
): Unsubscribe {
  const db = getFirestoreDB();
  if (!db) { connection(false); return () => {}; }
  return onSnapshot(collection(db, "users", userId, "job_changes"),
    { includeMetadataChanges: true },
    snapshot => {
      for (const change of snapshot.docChanges()) {
        const signal = parseJobSignal(change.doc.data());
        if (signal) {
          if (change.type === "removed") removed?.(signal);
          else changed(signal);
        }
      }
      connection(!snapshot.metadata.fromCache);
    },
    () => connection(false),
  );
}
